import React from 'react';
import {
  ShieldCheck,
  Send,
  Lock,
  Mail,
  ListChecks,
  Sparkles,
  Users,
  Clock,
  HeartHandshake,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import useInView from '../hooks/useInView';

const CONTACT_EMAIL = 'contact@romindkids.tech';

const testingEmailBody = `Parent / Guardian Name:
Parent / Guardian Email:
Country:
Child Age Group: 9-11 or 12-14
Device Type: Android / iOS / Both
Main Interest Area: Child experience / Safety feedback / App design feedback / Clubs & challenges / General testing
Optional Message:
I confirm that I am a parent or legal guardian: Yes`;

const TESTING_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('ROMIND KIDS Testing Request')}&body=${encodeURIComponent(testingEmailBody)}`;

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const StepCard = ({ number, title, children, icon: Icon }) => (
  <div className="glass-card p-5 sm:p-6 h-full border border-white/10 hover:border-purple-400/25 transition-all duration-300">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-400/25 flex items-center justify-center">
        {Icon ? <Icon size={16} className="text-purple-300" /> : <span className="text-purple-300 text-sm font-bold">{number}</span>}
      </div>
      <div>
        <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-white/35">Step {number}</p>
        <h3 className="font-outfit font-semibold text-white text-base">{title}</h3>
      </div>
    </div>
    <div className="text-white/55 text-sm leading-relaxed">{children}</div>
  </div>
);

const InfoCard = ({ title, children, icon: Icon, accent = 'purple' }) => {
  const accentClasses = {
    purple: 'bg-purple-500/15 border-purple-400/25 text-purple-300',
    cyan: 'bg-cyan-500/12 border-cyan-400/25 text-cyan-300',
    emerald: 'bg-emerald-500/12 border-emerald-400/25 text-emerald-300',
    rose: 'bg-rose-500/10 border-rose-400/20 text-rose-300',
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${accentClasses[accent]}`}>
          <Icon size={16} />
        </div>
        <h3 className="font-outfit font-semibold text-white text-base">{title}</h3>
      </div>
      <div className="text-white/55 text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const TestingSignUp = () => {
  const testingDetails = [
    'Parent / Guardian full name',
    'Parent / Guardian email',
    'Country',
    'Child age group: 9–11 or 12–14',
    'Device type: Android, iOS, or both',
    'Main interest area: child experience, safety feedback, app design feedback, clubs & challenges feedback, or general testing',
    'Optional message',
    'Confirmation that you are a parent or legal guardian',
  ];

  const focusAreas = [
    'Child experience and first impressions',
    'Safety and parent trust feedback',
    'App design and visual clarity',
    'Clubs, challenges, and creative mechanics',
    'General early testing interest',
  ];

  return (
    <main className="bg-[#05050A] min-h-screen">
      {/* Hero */}
      <section className="relative pt-28 pb-6 overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-purple-600/18 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-500/12 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-24 right-10 w-40 h-40 bg-pink-500/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-7 lg:gap-10 items-start">
              <div>
                <SectionLabel>Early Testing Program</SectionLabel>
                <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-4 tracking-tight leading-[1.05] max-w-4xl">
                  Join the first<br />
                  <span className="gradient-text">ROMIND KIDS explorers.</span>
                </h1>


                <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-3xl">
                  ROMIND KIDS is preparing its first preview path for families. At this stage, testing requests are collected by email so every parent message can be reviewed carefully before the early access list is formed.
                </p>
              </div>

              <div className="glass-card p-5 sm:p-6 border border-purple-400/20 bg-white/[0.045] lg:mt-1">
                <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-2">Start here</p>
                <h2 className="font-outfit font-black text-2xl sm:text-3xl text-white tracking-tight mb-3">
                  Request testing access now.
                </h2>
                <p className="text-white/55 text-sm leading-relaxed mb-5">
                  Send a direct email request and wait for our reply. Selected parents or guardians will receive the next steps when testing opens.
                </p>
                <a
                  data-testid="testing-hero-card-cta"
                  href={TESTING_MAILTO}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm bg-gradient-to-r from-purple-600/95 to-fuchsia-500/90 border border-purple-300/55 text-white hover:from-purple-500 hover:to-fuchsia-500 transition-all shadow-lg shadow-purple-900/35"
                >
                  Request Early Testing Access <Send size={14} />
                </a>
                <p className="mt-4 text-white/35 text-xs leading-relaxed">
                  Email: <span className="text-cyan-200">{CONTACT_EMAIL}</span>
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Email instruction section */}
      <section data-testid="testing-email-instructions" className="pt-4 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <FadeIn className="lg:col-span-3">
            <div className="glass-card p-6 sm:p-8 border border-purple-500/15">
              <div className="mb-8">
                <SectionLabel>Testing Access</SectionLabel>
                <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white tracking-tight mb-3">
                  <span className="gradient-text">Request early testing</span> by email.
                </h2>
                <p className="text-white/55 text-sm sm:text-base leading-relaxed max-w-2xl">
                  We are keeping the first testing list personal and carefully curated. Instead of a public form, parents and guardians can send a direct request to the ROMIND KIDS team.
                </p>
              </div>

              <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 mb-8">
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-cyan-300 mb-2">Email address</p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-outfit text-xl sm:text-2xl font-bold text-white hover:text-cyan-300 transition-colors break-all"
                >
                  {CONTACT_EMAIL}
                </a>
                <p className="text-white/40 text-xs mt-2">
                  Subject line: <span className="text-white/65">ROMIND KIDS Testing Request</span>
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StepCard number="1" title="Send us an email" icon={Mail}>
                  Write to <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">{CONTACT_EMAIL}</a> and tell us that you would like your family to be considered for the early testing list.
                </StepCard>

                <StepCard number="2" title="Use the testing subject" icon={ListChecks}>
                  Please use the subject <span className="text-white/80 font-medium">ROMIND KIDS Testing Request</span> so we can separate testing messages from general contact requests.
                </StepCard>

                <StepCard number="3" title="Include the details" icon={CheckCircle}>
                  Send only the parent-facing information listed below. This helps us understand the device, country, age group, and type of feedback your family may be able to provide.
                </StepCard>

                <StepCard number="4" title="Wait for our reply" icon={Clock}>
                  After we receive your request, the ROMIND KIDS team will review it. Selected parents or guardians will receive the next steps when testing opportunities become available.
                </StepCard>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={150} className="lg:col-span-2">
            <div className="space-y-4">
              <InfoCard title="Privacy note for parents" icon={Lock} accent="cyan">
                <p>
                  Please do <strong className="text-white/80">not</strong> send sensitive child information. Do not include a child’s full name, school name, exact address, phone number, or precise location.
                </p>
              </InfoCard>

              <InfoCard title="Curated early access" icon={ShieldCheck} accent="emerald">
                <p>
                  Early testing is intentionally limited to keep the experience thoughtful, safe, and useful. Sending a request does not guarantee a testing seat, but every message can help us understand interest from families.
                </p>
              </InfoCard>

              <InfoCard title="What testing may include" icon={Sparkles} accent="purple">
                <ul className="space-y-2">
                  <li>• Parent-supervised early access</li>
                  <li>• Feedback on creative features and design</li>
                  <li>• Feedback on clubs, challenges, and safety flows</li>
                  <li>• Program-related communication only</li>
                </ul>
              </InfoCard>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* What to include */}
      <section data-testid="testing-details-section" className="py-12 bg-[#080714]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <FadeIn>
            <div className="glass-card p-6 sm:p-8 h-full">
              <SectionLabel>What to Include</SectionLabel>
              <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">
                Details for your testing request.
              </h2>
              <p className="text-white/50 text-sm leading-relaxed mb-6">
                To help us review early testing interest properly, please include these details in your email:
              </p>
              <div className="space-y-3">
                {testingDetails.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-purple-500/20 border border-purple-500/35 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-300" />
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={150}>
            <div className="glass-card p-6 sm:p-8 h-full">
              <SectionLabel>Useful Feedback Areas</SectionLabel>
              <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">
                What families can help us understand.
              </h2>
              <p className="text-white/50 text-sm leading-relaxed mb-6">
                You can mention the area where your family would be most interested in giving feedback:
              </p>
              <div className="space-y-3">
                {focusAreas.map((item) => (
                  <div key={item} className="p-3 rounded-xl bg-white/4 border border-white/8 text-white/60 text-sm">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why children's feedback matters */}
      <section data-testid="children-feedback-section" className="relative py-20 overflow-hidden">
        <div className="absolute top-1/2 left-1/3 w-80 h-80 bg-fuchsia-600/12 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="glass-card p-6 sm:p-10 border border-cyan-400/10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-cyan-500/12 border border-cyan-400/25 flex items-center justify-center">
                  <Users size={18} className="text-cyan-300" />
                </div>
                <div>
                  <SectionLabel>Why Children’s Feedback Matters</SectionLabel>
                  <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white tracking-tight">
                    Professional testers check function. Children reveal experience.
                  </h2>
                </div>
              </div>

              <div className="space-y-5 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  Professional testers can check whether an app works correctly. But only children can honestly show whether the app feels alive, exciting, understandable, and worth returning to.
                </p>
                <p>
                  ROMIND KIDS is being created for children — their creativity, friendships, emotions, interests, and digital comfort. That is why early testing should include real children with parent or guardian involvement. Their reactions matter: what feels inspiring, what feels confusing, what feels boring, and what makes them want to explore more.
                </p>
                <p>
                  Many apps are tested mainly by adults before release. ROMIND KIDS wants to take a different path: children should help us understand the real experience before launch, because they are the ones who will actually live inside this creative universe.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="first-explorers-cta" className="relative py-20 bg-[#080714] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-transparent to-cyan-900/10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-500/12 border border-purple-400/25 mb-6">
              <HeartHandshake size={28} className="text-purple-300" />
            </div>

            <SectionLabel>First Explorers</SectionLabel>
            <h2 className="font-outfit font-black text-4xl sm:text-5xl text-white mb-5 tracking-tight leading-tight">
              Your child can help define<br />
              <span className="gradient-text">a new digital universe.</span>
            </h2>

            <p className="text-white/58 text-base sm:text-lg leading-relaxed mb-5 max-w-3xl mx-auto">
              Selected children may become among the first young explorers of ROMIND KIDS — not just testers, but early voices helping shape a safer creative social universe for children.
            </p>
            <p className="text-white/50 text-sm sm:text-base leading-relaxed mb-8 max-w-3xl mx-auto">
              For parents, this is an opportunity to support a platform built not around endless scrolling, but around creativity, friendship, emotional comfort, safety, and meaningful participation.
            </p>

            <a
              data-testid="testing-final-cta"
              href={TESTING_MAILTO}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm bg-purple-600/85 border border-purple-400/40 text-white hover:bg-purple-500 transition-all shadow-lg shadow-purple-900/30"
            >
              Request Early Testing Access <ArrowRight size={15} />
            </a>

            <p className="mt-5 text-white/35 text-xs tracking-[0.12em] uppercase">
              The first explorers help define the universe.
            </p>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default TestingSignUp;
